import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import type { components } from '../../../api/types/api';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';
import type {
  BankImportDraftResponseDto,
  BankImportParseResponseDto,
  BankImportRequestDto,
  BankImportResponseDto,
  DeleteBankStatementDraftResponseDto,
  GetPdfGenerationBatchesArgs,
  GetPdfGenerationVoucherStatusArgs,
  PdfGenerationBatchStatusDto,
  PdfGenerationBatchesResponseDto,
  PdfGenerationVoucherStatusDto,
  SaveBankStatementDraftRequestDto,
  SaveBankStatementDraftResponseDto,
  SaveBankStatementTransactionsRequestDto,
  SaveBankStatementTransactionsResponseDto,
} from '../types/BankImport';

type SaveImportDraftRequestDto = components['schemas']['SaveImportDraftRequestDto'];

/** OpenAPI types jsonData as Record<string, never>; bridge for the typed bank draft body. */
const toSaveImportDraftRequest = (
  body: SaveBankStatementDraftRequestDto,
): SaveImportDraftRequestDto => ({
  ...body,
  jsonData: body.jsonData as SaveImportDraftRequestDto['jsonData'],
});

export const bankImportApi = createApi({
  reducerPath: 'bankImportApi',
  baseQuery: nestBaseQuery,
  tagTypes: ['BankStatementDraft'],
  endpoints: (builder) => ({
    parseBankStatement: builder.mutation<BankImportParseResponseDto, BankImportRequestDto>({
      query: (body) =>
        client.POST('/api/v1/imports/bank-statement', {
          body: {
            key: body.key,
            ...(body.password ? { password: body.password } : {}),
          },
        }),
      // Modal refetches draft explicitly after parse — avoid racing an empty invalidate refetch.
    }),
    getBankStatementDraft: builder.query<BankImportResponseDto, void>({
      query: () => client.GET('/api/v1/imports/drafts/bank-statement'),
      providesTags: ['BankStatementDraft'],
      // Keep upserted draft across review → import navigation so "Saved as Draft" can show.
      keepUnusedDataFor: 60,
    }),
    deleteBankStatementDraft: builder.mutation<DeleteBankStatementDraftResponseDto, void>({
      query: () =>
        client.DELETE('/api/v1/imports/drafts/{importType}', {
          params: { path: { importType: 'BANK_STATEMENT' } },
        }),
      invalidatesTags: ['BankStatementDraft'],
    }),
    saveBankStatementDraft: builder.mutation<
      SaveBankStatementDraftResponseDto,
      SaveBankStatementDraftRequestDto
    >({
      query: (arg) =>
        client.POST('/api/v1/imports/drafts/save', {
          body: toSaveImportDraftRequest(arg),
        }),
      // Cache is updated explicitly after save in useBankImport (upsertQueryData).
      // Do not invalidate here — that can race and wipe the upsert before Back lands on the modal.
    }),
    /** Persists validated rows as Bank Payment / Bank Receipt vouchers (atomic). */
    saveBankStatementTransactions: builder.mutation<
      SaveBankStatementTransactionsResponseDto,
      SaveBankStatementTransactionsRequestDto
    >({
      query: (body) =>
        client.POST('/api/v1/imports/bank-statement/save', {
          body,
        }),
      // Draft is deleted only after a successful import (finalizeSuccessfulImport).
      // Do not invalidate here — failed validation/save must keep the draft detectable
      // on the import modal ("Saved as Draft") and avoid racing upsertQueryData.
    }),
    /**
     * Poll aggregate PDF-generation progress for one save batch (`pdfBatchId` from save response).
     * Built for the import success progress bar (completed/total + counts per status).
     */
    getPdfGenerationBatchStatus: builder.query<PdfGenerationBatchStatusDto, string>({
      query: (batchId) =>
        client.GET('/api/v1/imports/bank-import/pdf-generation/batch/{batchId}/status', {
          params: { path: { batchId } },
        }),
    }),
    /**
     * Check PDF-generation status for one payment/receipt voucher from bank-statement/save.
     */
    getPdfGenerationVoucherStatus: builder.query<
      PdfGenerationVoucherStatusDto,
      GetPdfGenerationVoucherStatusArgs
    >({
      query: ({ voucherId, voucherType }) =>
        client.GET('/api/v1/imports/bank-import/pdf-generation/voucher/{voucherId}/status', {
          params: {
            path: { voucherId },
            query: { voucherType },
          },
        }),
    }),
    /**
     * List PDF-generation batches for the current company/FY (defaults to today's IST day).
     * One row per batchId with aggregate progress.
     */
    getPdfGenerationBatches: builder.query<
      PdfGenerationBatchesResponseDto,
      GetPdfGenerationBatchesArgs
    >({
      query: ({ voucherType, jobType }) =>
        client.GET('/api/v1/imports/bank-import/pdf-generation/batches', {
          params: {
            query: {
              voucherType,
              ...(jobType ? { jobType } : {}),
            },
          },
        }),
    }),
  }),
});

export const {
  useParseBankStatementMutation,
  useLazyGetBankStatementDraftQuery,
  useGetBankStatementDraftQuery,
  useDeleteBankStatementDraftMutation,
  useSaveBankStatementDraftMutation,
  useSaveBankStatementTransactionsMutation,
  useGetPdfGenerationBatchStatusQuery,
  useLazyGetPdfGenerationBatchStatusQuery,
  useGetPdfGenerationVoucherStatusQuery,
  useLazyGetPdfGenerationVoucherStatusQuery,
  useGetPdfGenerationBatchesQuery,
  useLazyGetPdfGenerationBatchesQuery,
} = bankImportApi;
